/**
 * Verificação de integridade do IndexedDB.
 *
 * De vez em quando o LevelDB do IndexedDB do Chromium (Electron) não carrega — por corrupção em disco (shutdown sujo,
 * OneDrive/drive de rede, antivírus) ou porque o motor está bloqueado. Quando isso acontece o app fica inutilizável.
 *
 * Esta função detecta o problema em dois níveis:
 *
 * 1. Probe genérico: abre um DB descartável, escreve e lê um registro. Pega falha/bloqueio do próprio motor de IndexedDB.
 * 2. DBs reais do app: tenta abrir cada banco Dexie já existente. Pega corrupção/bloqueio de um store específico.
 *
 * Não apaga nada — apenas reporta saúde. A recuperação é via reinício do app.
 */

import { IS_BINARY } from '@/constants';
import * as Sentry from '@sentry/react';

const PROBE_DB_NAME = '__idb_healthcheck__';
const PROBE_STORE = 'probe';
// 3s estourava sob carga do Feed Link sem o banco estar corrompido; 15s só passa com o motor travado de verdade.
const OP_TIMEOUT_MS = 15_000;
const TIMEOUT_MESSAGE = 'IndexedDB timeout';

// Bancos Dexie usados pelo app (ver contexts/sync-* e api/db/indexedDB).
const APP_DB_NAMES = [
  'feed',
  'feed:sync-feed-link',
  'feed:sync-identification-management',
  'feed:offline-data',
  'feed:spreadsheets',
  'feed:logs-identification-management'
];

function withTimeout<T>(promise: Promise<T>, ms = OP_TIMEOUT_MS): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(TIMEOUT_MESSAGE)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

/** Abre um banco e resolve/rejeita; trata 'blocked' como falha. */
function openDatabase(name: string, version?: number, onUpgrade?: (db: IDBDatabase) => void): Promise<IDBDatabase> {
  return new Promise<IDBDatabase>((resolve, reject) => {
    let request: IDBOpenDBRequest;

    try {
      request = version != null ? indexedDB.open(name, version) : indexedDB.open(name);
    } catch (error) {
      reject(error);
      return;
    }

    request.onupgradeneeded = () => {
      try {
        onUpgrade?.(request.result);
      } catch (error) {
        reject(error);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error(`Falha ao abrir IndexedDB "${name}"`));
    request.onblocked = () => reject(new Error(`IndexedDB "${name}" bloqueado`));
  });
}

/** Probe genérico: cria/escreve/lê um DB descartável. */
async function probeGenericIndexedDb(): Promise<void> {
  if (typeof indexedDB === 'undefined' || !indexedDB) {
    throw new Error('IndexedDB indisponível neste contexto');
  }

  const db = await withTimeout(
    openDatabase(PROBE_DB_NAME, 1, (d) => {
      if (!d.objectStoreNames.contains(PROBE_STORE)) {
        d.createObjectStore(PROBE_STORE);
      }
    })
  );

  try {
    await withTimeout(
      new Promise<void>((resolve, reject) => {
        const tx = db.transaction(PROBE_STORE, 'readwrite');
        const store = tx.objectStore(PROBE_STORE);
        store.put(Date.now(), 'ping');

        const getReq = store.get('ping');
        getReq.onsuccess = () => resolve();
        getReq.onerror = () => reject(getReq.error ?? new Error('Falha ao ler probe'));
        tx.onerror = () => reject(tx.error ?? new Error('Falha na transação do probe'));
      })
    );
  } finally {
    db.close();
  }
}

/** Abre cada banco real do app que já existe e confirma que não está corrompido. */
async function probeAppDatabases(): Promise<void> {
  // indexedDB.databases() pode não existir em todos os runtimes; se faltar,
  // pulamos esta etapa (o probe genérico ainda cobre o motor).
  if (typeof indexedDB.databases !== 'function') return;

  const existing = await withTimeout(indexedDB.databases());
  const existingNames = new Set(existing.map((d) => d.name).filter(Boolean) as string[]);

  for (const name of APP_DB_NAMES) {
    if (!existingNames.has(name)) continue;

    // Abre na versão atual (sem version) — só verifica acesso, não altera schema.
    const db = await withTimeout(openDatabase(name));
    db.close();
  }
}

/**
 * Retorna true se o IndexedDB estiver saudável, false caso contrário. Nunca lança — qualquer erro é tratado como "não
 * saudável".
 */
let reported = false;

export async function checkIndexedDbHealth(): Promise<boolean> {
  const startedAt = performance.now();

  try {
    await probeGenericIndexedDb();
    await probeAppDatabases();
    return true;
  } catch (error) {
    const durationMs = Math.round(performance.now() - startedAt);
    const reason = error instanceof Error && error.message === TIMEOUT_MESSAGE ? 'timeout' : 'error';

    console.error(`[idb-health] IndexedDB não saudável (${reason} após ${durationMs}ms):`, error);

    // Reporta a 1ª falha por sessão (a checagem roda a cada 15s — sem o guard
    // viraria spam). É o que mede a taxa real de falha por plataforma.
    if (!reported) {
      reported = true;
      Sentry.captureException(error, {
        tags: { area: 'indexeddb', platform: IS_BINARY ? 'electron' : 'web', reason },
        extra: { durationMs }
      });
    }

    return false;
  }
}
